import { useLocation, useSearchParams } from "react-router-dom";

import { startGoogleSignIn } from "../lib/api";

/**
 * The title page.
 *
 * Also the only place that can explain a failed sign-in, since the OAuth
 * callback bounces back here with `?authError=<code>`.
 */
export function Landing() {
  const location = useLocation();
  const [params] = useSearchParams();

  const authError = params.get("authError");

  // RequireAuth stashes where the reader was heading, so sign-in returns them
  // there rather than dumping them on the feed.
  const intended = (location.state as { from?: string } | null)?.from;
  const next = intended && intended.startsWith("/") ? intended : "/scrapbook";

  return (
    <div className="grain relative min-h-dvh">
      <main className="mx-auto flex min-h-dvh max-w-3xl flex-col justify-center px-safe py-12">
        {authError ? <AuthErrorNotice code={authError} /> : null}

        <header className="text-center">
          <p className="field-label text-ink-soft">Established this afternoon</p>
          <h1 className="mt-4 display-1 text-[3.25rem] leading-[0.95] text-ink sm:text-7xl">
            Foundling
          </h1>
          <div className="hairline mx-auto my-6 max-w-sm" />
          <p className="mx-auto max-w-xl font-prose text-xl leading-relaxed text-ink sm:text-[1.375rem]">
            Photograph the things you come across — a tree, a bird, a strange door. Foundling works
            out what they are, mounts each one as a page in a scrapbook, and files it into a
            collection worth completing.
          </p>
        </header>

        <div className="mt-10 flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={() => startGoogleSignIn(next)}
            className="touch inline-flex items-center gap-3 border-2 border-ink bg-card px-5 py-3 font-label text-base font-medium tracking-wide text-ink shadow-sheet transition-transform duration-200 hover:bg-paper-deep active:scale-[0.98]"
          >
            <GoogleMark />
            Sign in with Google
          </button>
          <p className="max-w-sm text-center font-prose text-sm leading-snug text-ink-soft">
            Your finds are yours. Foundling keeps your photographs, your notes, and nothing else.
          </p>
        </div>

        <section className="mt-16">
          <h2 className="field-label text-center text-ink-soft">How it goes</h2>
          <ol className="mt-6 grid gap-6 sm:grid-cols-3">
            <Step
              index="I"
              title="You photograph it"
              body="Point your phone at whatever caught your eye. One photograph is enough."
              motif="camera"
            />
            <Step
              index="II"
              title="Claude names it"
              body="A determination, a field note, and the structured details a guide would print."
              motif="lens"
            />
            <Step
              index="III"
              title="It gets filed"
              body="Into a collection for your area — with a list of what else is worth finding there."
              motif="shelf"
            />
          </ol>
        </section>

        <section className="sheet grain-strong relative mt-14 p-6 sm:p-8">
          <p className="field-label text-ochre">A specimen page</p>
          <h2 className="mt-3 display-3 text-3xl leading-tight text-ink">
            Arboreal Wisconsin
          </h2>
          <p className="mt-1 font-prose text-lg italic text-ink-soft">7 of 16 trees found</p>
          <div className="mt-4 flex items-stretch gap-[3px]" aria-hidden="true">
            {Array.from({ length: 16 }, (_, index) => (
              <span
                key={index}
                className={
                  index < 7 ? "h-2.5 flex-1 border border-teal bg-teal" : "h-2.5 flex-1 border border-rule"
                }
              />
            ))}
          </div>
          <p className="mt-5 max-w-prose font-prose leading-relaxed text-ink-soft">
            Collections make themselves. Find a tree in Wisconsin and Foundling opens a volume for
            the trees of Wisconsin, then suggests the fifteen others you might reasonably hope to
            come across.
          </p>
        </section>

        <footer className="mt-16 text-center">
          <p className="font-label text-[0.625rem] tracking-[0.22em] text-ink-soft uppercase">
            Determinations by Claude · Not to be relied upon for anything you intend to eat
          </p>
        </footer>
      </main>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Sign-in failures arrive as an opaque code in the query string. Translate the
 * ones we know, and never show a bare code without a sentence beside it.
 */
function AuthErrorNotice({ code }: { code: string }) {
  const explanation = explainAuthError(code);

  return (
    <div
      role="alert"
      className="sheet grain relative mb-10 border-l-2 border-l-rust px-5 py-4"
    >
      <p className="field-label text-rust">Sign-in didn't finish</p>
      <p className="mt-2 max-w-prose font-prose text-[1.0625rem] leading-relaxed text-ink">
        {explanation}
      </p>
      <p className="mt-2 font-label text-[0.625rem] tracking-widest text-ink-soft uppercase">
        Code: {code}
      </p>
    </div>
  );
}

function explainAuthError(code: string): string {
  switch (code) {
    case "access_denied":
    case "denied":
      return "You cancelled at Google's screen, or declined the permission. Nothing was shared, and you can try again whenever you like.";
    case "state_mismatch":
    case "invalid_state":
      return "The sign-in took too long, or was started in another tab. Starting again from this page should work.";
    case "expired":
    case "expired_state":
      return "That sign-in attempt timed out. Have another go.";
    case "no_email":
    case "email_missing":
      return "Google didn't share an email address, and Foundling needs one to keep your scrapbook together.";
    case "not_configured":
      return "Google sign-in isn't set up on this server yet. If it's yours, check the OAuth credentials.";
    case "server_error":
      return "Something failed on our side while finishing the sign-in. It usually works on a second attempt.";
    default:
      return "Something went wrong between here and Google, and we're not sure what. Trying again is the best move; if it keeps happening, the code below is the useful part.";
  }
}

function Step({
  index,
  title,
  body,
  motif,
}: {
  index: string;
  title: string;
  body: string;
  motif: "camera" | "lens" | "shelf";
}) {
  return (
    <li className="sheet grain relative p-5">
      <p className="display-4 text-3xl leading-none text-ochre">
        {index}
      </p>
      <svg viewBox="0 0 48 48" aria-hidden="true" className="mt-3 h-12 w-12 opacity-85" fill="none" strokeWidth="1.4">
        {motif === "camera" ? (
          <>
            <rect x="4" y="13" width="40" height="26" stroke="var(--color-rule)" />
            <circle cx="24" cy="26" r="9" stroke="var(--color-ochre)" />
            <circle cx="24" cy="26" r="3.5" stroke="var(--color-ink-soft)" />
            <path d="M17 13l3-4h8l3 4" stroke="var(--color-rule)" />
          </>
        ) : motif === "lens" ? (
          <>
            <circle cx="21" cy="20" r="13" stroke="var(--color-rule)" />
            <path d="M14 20h14M21 13v14" stroke="var(--color-teal)" strokeDasharray="2 3" />
            <path d="M31 30l11 11" stroke="var(--color-ochre)" strokeWidth="2.6" strokeLinecap="round" />
          </>
        ) : (
          <>
            <rect x="5" y="10" width="9" height="28" stroke="var(--color-teal)" />
            <rect x="17" y="10" width="9" height="28" stroke="var(--color-rule)" />
            <rect x="29" y="10" width="9" height="28" stroke="var(--color-rule)" />
            <path d="M4 38h40" stroke="var(--color-ink-soft)" />
          </>
        )}
      </svg>
      <h3 className="mt-4 display-5 text-xl leading-tight text-ink">{title}</h3>
      <p className="mt-1.5 font-prose leading-relaxed text-ink-soft">{body}</p>
    </li>
  );
}

/** Google's mark, drawn rather than fetched. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-5">
      <path
        fill="#4285F4"
        d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.4a5.5 5.5 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.6-5.2 3.6-8.7Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3a7.2 7.2 0 0 1-10.7-3.8H1.3v3.1A12 12 0 0 0 12 24Z"
      />
      <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.3a12 12 0 0 0 0 10.8l4-3.1Z" />
      <path
        fill="#EA4335"
        d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1A7.2 7.2 0 0 1 12 4.8Z"
      />
    </svg>
  );
}
